import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { theme } from 'app/theme/index'
import { EXTERNAL_URL_REGEX } from 'app/utils/customFunctions'
import { Link } from 'gatsby'
import React, { memo } from 'react'
import { Link as Scroll } from 'react-scroll'

export interface Props {
  ariaLabel?: string
  className?: string
  file?: string
  gTagEvent?: string
  label: string
  onClick?: () => void
  onKeyDown?: any
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  noActions?: boolean
  rel?: string
  role?: string
  scroll?: { duration?: number; offset?: number; to: string }
  target?: string
  URL?: string
  variant?: Variant
}

export const Button = memo(function Button({
  ariaLabel,
  className,
  file,
  gTagEvent,
  label = 'Button',
  onClick,
  onKeyDown,
  onMouseEnter,
  onMouseLeave,
  noActions,
  rel,
  role,
  scroll,
  target,
  URL,
  variant = 'default',
}: Props) {
  const buttonClassName = `${className ? className : undefined}`
  const buttonDOM = <Inner>{label}</Inner>

  const externalURL = URL && EXTERNAL_URL_REGEX.test(URL)

  return (
    <>
      {file ? (
        <Anchor
          aria-label={ariaLabel}
          className={buttonClassName}
          href={file}
          onClick={() =>
            gTagEvent
              ? typeof window.gtag !== 'undefined' &&
                window.gtag('event', 'Click', {
                  event_category: 'Website',
                  event_label: gTagEvent,
                })
              : null
          }
          onKeyDown={onKeyDown}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          rel={rel}
          role={role}
          tabIndex={0}
          target="_blank"
          variant={variant}
        >
          {buttonDOM}
        </Anchor>
      ) : scroll?.to ? (
        <ButtonScroll
          activeClass="active"
          aria-label={ariaLabel}
          className={buttonClassName}
          duration={scroll.duration}
          offset={scroll.offset}
          onClick={onClick}
          onKeyDown={onKeyDown}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          rel={rel}
          role={role}
          smooth={true}
          tabIndex={0}
          to={scroll.to}
          variant={variant}
        >
          {buttonDOM}
        </ButtonScroll>
      ) : (
        <>
          {!externalURL ? (
            target === 'tel' || target === 'mailto' ? (
              <Anchor
                aria-label={ariaLabel}
                className={buttonClassName}
                href={`${target}:${URL}`}
                onClick={onClick}
                onKeyDown={onKeyDown}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                rel="noopener"
                role={role}
                tabIndex={0}
                variant={variant}
              >
                {buttonDOM}
              </Anchor>
            ) : onClick != undefined ? (
              <Static
                aria-label={ariaLabel}
                className={buttonClassName}
                onClick={onClick}
                onKeyDown={onKeyDown}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                role={role}
                tabIndex={0}
                variant={variant}
              >
                {buttonDOM}
              </Static>
            ) : noActions ? (
              <Static
                aria-label={ariaLabel}
                className={buttonClassName}
                variant={variant}
              >
                {buttonDOM}
              </Static>
            ) : (
              <ButtonItem
                aria-label={ariaLabel}
                className={buttonClassName}
                onKeyDown={onKeyDown}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                role={role}
                tabIndex={0}
                to={URL ? URL : '#'}
                variant={variant}
              >
                {buttonDOM}
              </ButtonItem>
            )
          ) : (
            <Anchor
              aria-label={ariaLabel}
              className={buttonClassName}
              href={URL}
              onClick={() =>
                gTagEvent
                  ? typeof window.gtag !== 'undefined' &&
                    window.gtag('event', 'Click', {
                      event_category: 'Website',
                      event_label: gTagEvent,
                    })
                  : null
              }
              onKeyDown={onKeyDown}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              rel="noopener"
              role={role}
              tabIndex={0}
              target={target}
              variant={variant}
            >
              {buttonDOM}
            </Anchor>
          )}
        </>
      )}
    </>
  )
})

const Inner = styled.span`
  display: inline-block;
  position: relative;
  transition: 0.1s;
  z-index: 1;
`

const Style = css`
  display: inline-block;
  border: 0.0625rem solid ${theme.colors.variants.primaryDark};
  color: ${theme.colors.variants.primaryDark};
  cursor: pointer;
  font-family: ${theme.fontFamily.heading};
  font-size: 0.875rem;
  letter-spacing: 0.05em;
  line-height: 1.125rem;
  padding: 0.4375rem 2rem;
  position: relative;
  text-align: center;
  text-transform: uppercase;
  transition: 0.4s;
  &:before {
    content: '';
    width: 100%;
    background: ${theme.colors.variants.primaryDark};
    position: absolute;
    top: 100%;
    left: 0;
    bottom: 0;
    transition: 0.4s ease-in-out;
  }
  &:hover {
    color: ${theme.colors.variants.neutralLight2};
    &:before {
      top: 0;
    }
  }
`

const Invert = css`
  ${Style}

  border-color: ${theme.colors.variants.neutralLight2};
  color: ${theme.colors.variants.neutralLight2};
  &:before {
    background: ${theme.colors.variants.neutralLight2};
  }
  &:hover {
    color: ${theme.colors.variants.primaryDark};
  }
`

const Flat = css`
  display: inline-block;
  color: ${theme.colors.variants.primaryDark};
  cursor: pointer;
  font-family: ${theme.fontFamily.heading};
  font-size: 0.9375rem;
  letter-spacing: 0.02em;
  line-height: 1.25rem;
  padding-bottom: 0.25rem;
  position: relative;
  text-transform: uppercase;
  &:before,
  &:after {
    content: '';
    height: 0.0625rem;
    background: ${theme.colors.variants.primaryDark};
    position: absolute;
    bottom: 0;
    left: 0;
    transition: 0.4s ease-in-out;
  }
  &:before {
    right: 0;
  }
  &:after {
    right: 100%;
    transition-delay: 0.3s;
  }
  &:hover {
    &:before {
      left: 100%;
    }
    &:after {
      right: 0;
    }
  }
`

const FlatInvert = css`
  ${Flat}

  color: ${theme.colors.variants.neutralLight1};
  &:before,
  &:after {
    background: ${theme.colors.variants.neutralLight1};
  }
`

const Anchor = styled.a<ContainerProps>`
  ${({ variant }) => {
    switch (variant) {
      case 'default':
        return Style
      case 'invert':
        return Invert
      case 'flat':
        return Flat
      case 'flat-invert':
        return FlatInvert
    }
  }}
`

const Static = styled.div<ContainerProps>`
  ${({ variant }) => {
    switch (variant) {
      case 'default':
        return Style
      case 'invert':
        return Invert
      case 'flat':
        return Flat
      case 'flat-invert':
        return FlatInvert
    }
  }}
`

const ButtonItem = styled(Link)<ContainerProps>`
  ${({ variant }) => {
    switch (variant) {
      case 'default':
        return Style
      case 'invert':
        return Invert
      case 'flat':
        return Flat
      case 'flat-invert':
        return FlatInvert
    }
  }}
`

const ButtonScroll = styled(Scroll)<ContainerProps>`
  ${({ variant }) => {
    switch (variant) {
      case 'default':
        return Style
      case 'invert':
        return Invert
      case 'flat':
        return Flat
      case 'flat-invert':
        return FlatInvert
    }
  }}
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'invert' | 'flat' | 'flat-invert'
